import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import EditAgencyModal from './components/EditAgencyModal'; // Import the modal
import Head from '../../layout/head/Head';
import Content from '../../layout/content/Content';
import {
    Block,
    BlockDes,
    BlockHeadContent,
    BlockTitle,
    BlockBetween,
    BlockHead,
    DataTableHead,
    DataTableItem,
    DataTableRow,
    Icon,
    PaginationComponent,
    PreviewAltCard,
    Row,
    Col,
    RSelect,
    DataTable,
    DataTableBody,
    UserAvatar,
    TooltipComponent
} from '../../components/Component';
import {
    Button,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledDropdown,
    Badge,
    Spinner
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom'; // Assuming you might need Link for actions
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { toast } from 'react-toastify';

const fetchAgenciesAPI = async (axios, search = "") => {
    const endpoint = search ? `/agency?search=${encodeURIComponent(search)}` : '/agency';
    const response = await axios.get(endpoint);
    // If it's an absolute path, use the full URL.

    return response.data;
};


const AgencyListPage = () => {
    const axios = useAxiosPrivate();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState("");
    const [onSearch, setOnSearch] = useState(false); // State for search bar visibility
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentAgency, setCurrentAgency] = useState(null);

    const searchRef = useRef(null);

    const { data: agencies, isLoading, error } = useQuery(
        ['agencies', searchText], // Include searchText in the query key
        () => fetchAgenciesAPI(axios, searchText), // Pass searchText to the fetch function
        { keepPreviousData: true } // Optional: keeps old data visible while new data loads
    );

    // Mutation for updating an agency
    const updateAgencyMutation = useMutation(
        async ({ agencyId, updateData }) => {
            // Transform updateData based on acceptedFields

            // Make the API call
            const response = await axios.patch(`/agency/${agencyId}`, updateData)
            return response.data;
        },
        {
            onSuccess: () => {
                // Invalidate and refetch agencies query to reflect the update
                queryClient.invalidateQueries(['agencies']);
                // Optionally, display a success notification
                toast.success('Agency updated successfully')
            },
            onError: (error) => {
                // Handle error, e.g., display a notification
                console.error("Error updating agency:", error);
                toast.error(`Error updating agency: ${error.message || 'An unexpected error occurred.'}`)
            },
        }
    );

    // Example function to trigger the mutation (can be called from a button, form, etc.)
    // const handleUpdateAgency = (agencyId, dataToUpdate) => {
    //     updateAgencyMutation.mutate({ agencyId, updateData: dataToUpdate });
    // };

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [smOption, setSmOption] = useState(false); // For mobile view options

    // Function to open the edit modal
    const handleEditAgency = (agency) => {
        setCurrentAgency(agency);
        setShowEditModal(true);
    };

    // Toggle search bar
    const toggleSearch = () => setOnSearch(!onSearch);

    // Handle search input change
    const handleSearchInputChange = (e) => {
        setSearchText(e.target.value);
        setCurrentPage(1); // Reset to first page on new search
    };

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = agencies ? agencies.slice(indexOfFirstItem, indexOfLastItem) : [];
    const totalItems = agencies ? agencies.length : 0;

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    if (isLoading) {
        return (
            <Content>
                <div className="text-center p-5">
                    <Spinner color="primary" />
                    <p>Loading agencies...</p>
                </div>
            </Content>
        );
    }

    if (error) {
        return (
            <Content>
                <PreviewAltCard>
                    <p className="text-danger text-center">Error fetching agencies: {error.message}</p>
                </PreviewAltCard>
            </Content>
        );
    }

    return (
        <React.Fragment>
            <Head title="Agencies" />
            <Content>
                <BlockHead size="sm">
                    <BlockBetween>
                        <BlockHeadContent>
                            <BlockTitle tag="h3" page>
                                Agencies
                            </BlockTitle>
                            <BlockDes className="text-soft">
                                <p>You have total {totalItems} agencies.</p>
                            </BlockDes>
                        </BlockHeadContent>
                        <BlockHeadContent>
                            <div className="toggle-wrap nk-block-tools-toggle">
                                <Button
                                    className={`btn-icon btn-trigger toggle-expand me-n1 ${smOption ? "active" : ""}`}
                                    onClick={() => setSmOption(!smOption)}
                                >
                                    <Icon name="menu-alt-r"></Icon>
                                </Button>
                                <div className="toggle-expand-content" style={{ display: smOption ? "block" : "none" }}>
                                    <ul className="nk-block-tools g-3">

                                        <li className="nk-block-tools-opt">
                                            <Button color="primary" onClick={() => { navigate('/add-agency') }}>
                                                <Icon name="plus"></Icon>
                                                <span>Add New Agency</span>
                                            </Button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </BlockHeadContent>
                    </BlockBetween>
                </BlockHead>

                <Block>
                    <DataTable className="card-stretch">
                        <div className="card-inner-group">
                            <div className="card-inner position-relative card-tools-toggle">
                                <div className="card-title-group">
                                    <div className="card-tools">
                                        {/* Bulk actions removed */}
                                    </div>
                                    <div className="card-tools me-n1">
                                        <ul className="btn-toolbar gx-1">
                                            <li>
                                                <a
                                                    href="#search"
                                                    onClick={(ev) => {
                                                        ev.preventDefault();
                                                        toggleSearch();
                                                        searchRef.current?.focus?.();
                                                    }}
                                                    className="btn btn-icon search-toggle toggle-search"
                                                >
                                                    <Icon name="search"></Icon>
                                                </a>
                                            </li>
                                            <li className="btn-toolbar-sep"></li>
                                            <li>
                                                {/* Placeholder for view settings */}
                                                <UncontrolledDropdown>
                                                    <DropdownToggle color="tranparent" className="btn btn-trigger btn-icon dropdown-toggle">
                                                        <Icon name="setting"></Icon>
                                                    </DropdownToggle>
                                                    <DropdownMenu end className="dropdown-menu-xs">
                                                        <ul className="link-check">
                                                            <li><span>Show</span></li>
                                                            {[10, 20, 50].map(num => (
                                                                <li className={itemsPerPage === num ? "active" : ""} key={num}>
                                                                    <DropdownItem tag="a" href="#dropdownitem" onClick={(ev) => { ev.preventDefault(); setItemsPerPage(num); setCurrentPage(1); }}>{num}</DropdownItem>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </DropdownMenu>
                                                </UncontrolledDropdown>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div className={`card-search search-wrap ${onSearch ? "active" : ""}`}>
                                    <div className="card-body">
                                        <div className="" style={{ display: "flex", alignItems: "center" }}>
                                            <input
                                                type="text"
                                                className="form-control border-transparent form-focus-none"
                                                placeholder="Search by agency name..."
                                                value={searchText}
                                                onChange={handleSearchInputChange}
                                                ref={searchRef}
                                                style={{ flexGrow: 1 }}
                                            />
                                            <Button
                                                className="search-close btn-icon"
                                                color='transparent'
                                                onClick={() => {
                                                    setSearchText("");
                                                    toggleSearch();
                                                }} // Close the search bar and clear the search text
                                            >
                                                <Icon name="cross"></Icon>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <DataTableBody>
                                <DataTableHead>
                                    <DataTableRow><span>Agency Name</span></DataTableRow>
                                    <DataTableRow size="mb"><span>Agency Phone</span></DataTableRow>
                                    <DataTableRow size="md"><span>Primary Contact</span></DataTableRow>
                                    <DataTableRow size="lg"><span>Email</span></DataTableRow>
                                    <DataTableRow size="md"><span>Status</span></DataTableRow>
                                    <DataTableRow className="nk-tb-col-tools text-end">Action</DataTableRow>
                                </DataTableHead>

                                {currentItems && currentItems.length > 0 ? currentItems.map((agency) => (
                                    <DataTableItem key={agency._id}>
                                        <DataTableRow>
                                            <Link to={`/agency-details/${agency._id}`}> {/* // TODO: Update link */}
                                                <div className="user-card">
                                                    {/* <UserAvatar theme="primary" text={agency.agency_name.substring(0,2)}></UserAvatar> */}
                                                    <div className="user-info">
                                                        <span className="tb-lead">{agency.agency_name} <span className="dot dot-success d-md-none ms-1"></span></span>
                                                        {/* <span>info@softnio.com</span> */}
                                                    </div>
                                                </div>
                                            </Link>
                                        </DataTableRow>
                                        <DataTableRow size="mb">
                                            <span>{agency.agency_phone || 'N/A'}</span>
                                        </DataTableRow>
                                        <DataTableRow size="md">
                                            <span>{agency.primary_contact ? `${agency.primary_contact.first_name} ${agency.primary_contact.last_name}` : 'N/A'}</span>
                                        </DataTableRow>
                                        <DataTableRow size="lg">
                                            <span>{agency.email || 'N/A'}</span>
                                        </DataTableRow>
                                        <DataTableRow size="md">
                                            <Badge color={agency.active ? "success" : "danger"} className="badge-dim">
                                                {agency.active ? "Active" : "Inactive"}
                                            </Badge>
                                        </DataTableRow>
                                        <DataTableRow className="nk-tb-col-tools">
                                            <ul className="nk-tb-actions gx-1">
                                                <li className="nk-tb-action-hidden">
                                                    <TooltipComponent
                                                        tag="a"
                                                        containerClassName="btn btn-trigger btn-icon"
                                                        id={`edit-${agency._id}`}
                                                        icon="edit-alt-fill"
                                                        direction="top"
                                                        text="Edit Agency"
                                                        onClick={() => handleEditAgency(agency)}
                                                    />
                                                </li>
                                                {/* TODO: Add more actions like delete, view */}
                                                <li>
                                                    <UncontrolledDropdown>
                                                        <DropdownToggle tag="a" className="dropdown-toggle btn btn-icon btn-trigger">
                                                            <Icon name="more-h"></Icon>
                                                        </DropdownToggle>
                                                        <DropdownMenu end>
                                                            <ul className="link-list-opt no-bdr">
                                                                <li><DropdownItem tag="a" href="#edit" onClick={(ev) => { ev.preventDefault(); handleEditAgency(agency); }}><Icon name="edit"></Icon><span>Edit</span></DropdownItem></li>
                                                                <li><DropdownItem tag="a" href="#view" onClick={(ev) => { ev.preventDefault(); /* TODO: Implement View */ alert(`View ${agency.agency_name}`); }}><Icon name="eye"></Icon><span>View Details</span></DropdownItem></li>
                                                            </ul>
                                                        </DropdownMenu>
                                                    </UncontrolledDropdown>
                                                </li>
                                            </ul>
                                        </DataTableRow>
                                    </DataTableItem>
                                )) : (
                                    <DataTableItem>
                                        <DataTableRow className="text-center">
                                            <td colSpan="7">No agencies found.</td>
                                        </DataTableRow>
                                    </DataTableItem>
                                )}
                            </DataTableBody>
                            <div className="card-inner">
                                {agencies && agencies.length > 0 ? (
                                    <PaginationComponent
                                        itemPerPage={itemsPerPage}
                                        totalItems={totalItems}
                                        paginate={paginate}
                                        currentPage={currentPage}
                                    />
                                ) : (
                                    <div className="text-center">
                                        <span className="text-silent">No agencies found</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </DataTable>
                </Block>
            </Content>
            {currentAgency && (
                <EditAgencyModal
                    show={showEditModal}
                    onHide={() => {
                        setShowEditModal(false);
                        setCurrentAgency(null); // Clear current agency when modal is hidden
                    }}
                    agency={currentAgency}
                    updateAgency={updateAgencyMutation.mutate}
                />
            )}
        </React.Fragment>
    );
};

export default AgencyListPage;